import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  TileGroup,
  Typography,
  Skeleton,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  TILES_TITLE,
  VIRTUAL_ASSISTANT_CONTAINER_TITLE,
  HAULAWAY_UNAVAILABLE,
  INSTALL_NOT_AVAILABLE,
  PROTECTION_EMPTY,
  BOPIS_ALERT,
  ADD_ON_CATEGORIES,
  ADD_ON_TYPES
} from '../constants';
import { TileButton } from './subcomponents/TileButton';
import { partsAndServicesDataModel } from './partsAndServicesDataModel';
import {
  getAddOns,
  getProducts,
  isExcludedProduct
} from '../utils';
import { usePartsAndServicesHook } from '../hooks/usePartsAndServicesHook';
import { track } from '../parts-and-services-analytics';

const LoadingSkeleton = () => (
  <div className="sui-mt-5 sui-mb-5">
    <Skeleton disablePadding grow>
      <SkeletonLine variant="multi" numberOfLines={2} />
    </Skeleton>
  </div>
);

const ServiceSection = ({
  items, type, userSelection, onSelectionChange, isBopisAndPickup, emptyMessage
}) => {
  if (!items?.length) {
    return <Alert status="warning">{emptyMessage}</Alert>;
  }

  return (
    <TileButton
      type={type}
      userSelection={userSelection}
      availableItems={items}
      onSelectionChange={onSelectionChange}
      isBopisAndPickup={isBopisAndPickup}
    />
  );
};

export const PartsAndServicesTiles = ({
  itemId,
  lineItemId,
  deliveryZip,
  selectedAddOns,
  setSelectedAddOns,
  productInfo,
  isBopisAndPickup
}) => {
  const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(true);
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const virtualAssistantCategories = useConfigService('virtualAssistantCategories') || '';
  const isVirtualAssistantCategory = isExcludedProduct(productInfo, virtualAssistantCategories);

  const { data } = usePartsAndServicesHook({
    itemIds: [itemId],
    storeZip: deliveryZip,
    isCustomerIdentified
  });

  const products = useMemo(() => getProducts(data), [data]);

  const productsMap = useMemo(() => {
    const map = new Map();
    if (products) {
      map.set(lineItemId, products.find((product) => product.itemId === itemId));
    }
    return map;
  }, [products, lineItemId, itemId]);

  const protectionItems = useMemo(() => (
    getAddOns({ productsMap, lineItemId, addOnCategories: ADD_ON_CATEGORIES.PROTECTION })
      ?.sort((a, b) => b?.termLength - a?.termLength)
  ), [productsMap, lineItemId]);

  const haulAwayItems = useMemo(() => (
    getAddOns({ productsMap, lineItemId, addOnCategories: ADD_ON_CATEGORIES.HAUL_AWAY, addOnTypes: ADD_ON_TYPES.HAUL_AWAY })
      ?.sort((a, b) => a?.price - b?.price)
  ), [productsMap, lineItemId]);

  const installItems = useMemo(() => (
    getAddOns({ productsMap, lineItemId, addOnCategories: ADD_ON_CATEGORIES.INSTALLATION })
  ), [productsMap, lineItemId]);

  useEffect(() => {
    if (!installItems && !haulAwayItems && !protectionItems) return;
    track('page-load');
    setShowLoadingSkeleton(false);
  }, [installItems, haulAwayItems, protectionItems]);

  const saveSelection = useCallback((key, value) => {
    setSelectedAddOns((prev) => {
      const next = {
        ...prev,
        [lineItemId]: { ...prev[lineItemId], [key]: value }
      };
      return next;
    });
  }, [lineItemId, setSelectedAddOns]);

  const userSelections = selectedAddOns?.[lineItemId] || {};

  return (
    <div className="sui-flex sui-justify-between sui-bg-primary
      sui-gap-4 sui-p-3 sui-flex-wrap sui-flex-col sm:sui-flex-row sui-w-full"
    >
      <Typography variant="h4">
        {isVirtualAssistantCategory ? VIRTUAL_ASSISTANT_CONTAINER_TITLE : TILES_TITLE}
      </Typography>
      {isBopisAndPickup && (
        <div className="sui-w-full">
          <Alert headline="Unavailable with Pickup" status="warning">{BOPIS_ALERT}</Alert>
        </div>
      )}
      <TileGroup
        aria-label="parts and services options"
        disableExclusive
        className="sui-flex sui-flex-col sui-gap-4 sui-font-bold sui-w-full"
      >
        {!isVirtualAssistantCategory && (showLoadingSkeleton ? <LoadingSkeleton /> : (
          <ServiceSection
            items={installItems}
            type="installation"
            userSelection={userSelections.installation}
            onSelectionChange={(item) => saveSelection('installation', item)}
            isBopisAndPickup={isBopisAndPickup}
            emptyMessage={INSTALL_NOT_AVAILABLE}
          />
        ))}
        {showLoadingSkeleton ? <LoadingSkeleton /> : (
          <ServiceSection
            items={haulAwayItems}
            type="haulAway"
            userSelection={userSelections.haulAway}
            onSelectionChange={(item) => saveSelection('haulAway', Array.isArray(item) ? item : (item ? [item] : null))}
            isBopisAndPickup={isBopisAndPickup}
            emptyMessage={HAULAWAY_UNAVAILABLE}
          />
        )}
        {showLoadingSkeleton ? <LoadingSkeleton /> : (
          <ServiceSection
            items={protectionItems}
            type="protection"
            userSelection={userSelections.protection}
            onSelectionChange={(item) => saveSelection('protection', item)}
            isBopisAndPickup={isBopisAndPickup}
            emptyMessage={PROTECTION_EMPTY}
          />
        )}
      </TileGroup>
    </div>
  );
};

PartsAndServicesTiles.propTypes = {
  itemId: PropTypes.string.isRequired,
  lineItemId: PropTypes.string.isRequired,
  deliveryZip: PropTypes.string.isRequired,
  selectedAddOns: PropTypes.object.isRequired,
  setSelectedAddOns: PropTypes.func.isRequired,
  productInfo: PropTypes.object,
  isBopisAndPickup: PropTypes.bool
};

PartsAndServicesTiles.dataModel = partsAndServicesDataModel;
PartsAndServicesTiles.displayName = 'PartsAndServicesTiles';
